import React, { useState, useEffect } from 'react';
import { useSetRecoilState, useRecoilState, useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  Modal,
  Upload,
  Spin,
  Divider,
  Popconfirm,
  Tag
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { reloadSpecialServicesAtom, notificationsAtom, userPrivilagesAtom } from '../../../../atoms/Atoms';
import Button from '../../../shared-components/buttons/Button';
import PopconfirmButton from '../../../shared-components/buttons/PopconfirmButton';
import AntdInput from '../../../shared-components/form/AntdInput';
import Textarea from '../../../shared-components/form/Textarea';
import AntdDatePicker from '../../../shared-components/form/AntdDatePicker';
import AntdInputNumber from '../../../shared-components/form/AntdInputNumber';
import api from '../../../../api/api';
import { localizeSpecialServiceDates, thousandAllowZero, specialServiceStatusToColor, specialServiceStatusToString } from '../../../../functions';

function SelectedSpecialServicesModal({ onClose, service }) {
  const { Dragger } = Upload;
  const userPrivilages = useRecoilValue(userPrivilagesAtom);
  const { t, i18n } = useTranslation();
  const setReloadSpecialServices = useSetRecoilState(reloadSpecialServicesAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [rejectionModal, setRejectionModal] = useState(false);
  const [rejectionMessage, setRejectionMessage] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const draggerProps = {
    name: 'file',
    multiple: false,
    showUploadList: true,
    maxCount: 1,
    fileList: data?.fileData ? [data.fileData] : [],
    beforeUpload: (file) => {
      setData({
        ...data,
        fileData: file
      });
      return false;
    },
    ondrop: (e) => {
    },
    onRemove: (e) => {
        setData({
        ...data,
        fileData: null
      });
    }
  };

  function valid() {
    let isValid = true;
    let tempData = {
      ...data
    };
    if (!data?.date) {
      isValid = false;
      tempData = {
        ...tempData,
        dateError: t('date_is_required')
      }
    } else {
      tempData = {
        ...tempData,
        dateError: ''
      }
    }

    if (data?.status >= 3) {
      if (!data?.startDate) {
        isValid = false;
        tempData = {
          ...tempData,
          startDateError: t('date_is_required')
        }
      } else {
        tempData = {
          ...tempData,
          startDateError: ''
        }
      }
  
      if (!data?.endDate) {
        isValid = false;
        tempData = {
          ...tempData,
          endDateError: t('date_is_required')
        }
      } else {
        tempData = {
          ...tempData,
          endDateError: ''
        }
      }
  
      if (data?.startDate && data?.endDate && dayjs(data?.endDate).isBefore(dayjs(data?.startDate))) {
        isValid = false;
        tempData = {
          ...tempData,
          endDateError: t('end_time_cannot_be_before_start')
        }
      } else if (data?.startDate && data?.endDate && dayjs(data?.startDate).isSame(dayjs(data?.endDate))) {
        isValid = false;
        tempData = {
          ...tempData,
          endDateError: t('start_and_end_times_cannot_be_the_same')
        }
      } else if (data?.startDate && data?.endDate) {
        tempData = {
          ...tempData,
          endDateError: ''
        }
      }
  
      if (data?.date && data?.actEmployeeSignedDate && dayjs(data?.date).isBefore(dayjs(data?.actEmployeeSignedDate))) {
        isValid = false;
        tempData = {
          ...tempData,
          actEmployeeSignedDateError: t('signed_date_must_be_after_request')
        }
      } else {
        tempData = {
          ...tempData,
          actEmployeeSignedDateError: ''
        }
      }
  
      if (data?.date && data?.firstTransitEmployeeSignedDate && dayjs(data?.date).isBefore(dayjs(data?.firstTransitEmployeeSignedDate))) {
        isValid = false;
        tempData = {
          ...tempData,
          firstTransitEmployeeSignedDateError: t('signed_date_must_be_after_request')
        }
      } else {
        tempData = {
          ...tempData,
          firstTransitEmployeeSignedDateError: ''
        }
      }

      if (data?.busNumber?.length === 0) {
        isValid = false;
        tempData = {
          ...tempData,
          busNumberError: t('amount_needs_to_be_greater_than_zero')
        }
      } else {
        const tempNum = parseInt(data?.busNumber, 10);
        if (tempNum === 0) {
          tempData = {
            ...tempData,
            busNumberError: t('amount_needs_to_be_greater_than_zero')
          }
        } else {
          tempData = {
            ...tempData,
            busNumberError: ''
          }
        }
      }
  
      if (data?.unitNumber?.length === 0) {
        isValid = false;
        tempData = {
          ...tempData,
          unitNumberError: t('field_is_required')
        }
      } else {
        tempData = {
          ...tempData,
          unitNumberError: ''
        }
      }

      if (data?.actEmployeeName?.length === 0) {
        isValid = false;
        tempData = {
          ...tempData,
          actEmployeeNameError: t('field_is_required')
        }
      } else {
        tempData = {
          ...tempData,
          actEmployeeNameError: ''
        }
      }

      if (data?.actEmployeeNumber?.length === 0) {
        isValid = false;
        tempData = {
          ...tempData,
          actEmployeeNumberError: t('field_is_required')
        }
      } else {
        tempData = {
          ...tempData,
          actEmployeeNumberError: ''
        }
      }

      if (data?.firstTransitEmployeeName?.length === 0) {
        isValid = false;
        tempData = {
          ...tempData,
          firstTransitEmployeeNameError: t('field_is_required')
        }
      } else {
        tempData = {
          ...tempData,
          firstTransitEmployeeNameError: ''
        }
      }

      if (!data?.actEmployeeSignedDate) {
        isValid = false;
        tempData = {
          ...tempData,
          actEmployeeSignedDateError: t('date_is_required')
        }
      } else {
        tempData = {
          ...tempData,
          actEmployeeSignedDateError: ''
        }
      }

      if (!data?.firstTransitEmployeeSignedDate) {
        isValid = false;
        tempData = {
          ...tempData,
          firstTransitEmployeeSignedDateError: t('date_is_required')
        }
      } else {
        tempData = {
          ...tempData,
          firstTransitEmployeeSignedDateError: ''
        }
      }
    }

    // console.log('valid', isValid);
    // console.log('valid', tempData);
    setData(tempData);
    return isValid;
  }

  function changeStatus(newStatus) {
    setLoading(true);
    api.put('sonnell/specialservice/status', {
      Id: data?.id,
      Status: newStatus,
      Message: rejectionMessage
    })
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setReloadSpecialServices(true);
          setData(localizeSpecialServiceDates(response.data.data));
          if (rejectionModal) {
            setRejectionModal(false);
          }
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
  }

  function update(status) {
    setLoading(true);
    const formdata = new FormData();
    formdata.append('file', data.fileData);
    formdata.append('modelString', JSON.stringify({
      ...data,
      date: dayjs(data?.date).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss'),
      startDate: data?.startDate ? dayjs(data?.startDate).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null,
      endDate: data?.endDate ? dayjs(data?.endDate).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null,
      actEmployeeSignedDate: data?.actEmployeeSignedDate ? dayjs(data?.actEmployeeSignedDate).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null,
      firstTransitEmployeeSignedDate: data?.firstTransitEmployeeSignedDate ? dayjs(data?.firstTransitEmployeeSignedDate).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null,
    }));
    api.put('sonnell/specialservice', formdata)
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setReloadSpecialServices(true);
          setData(localizeSpecialServiceDates(response.data.data));
          if (status) {
            changeStatus(status);
          }
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
  }

  function create() {
    setLoading(true);
    const formdata = new FormData();
    formdata.append('file', data.fileData);
    formdata.append('modelString', JSON.stringify({
      ...data,
      date: dayjs(data?.date).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss'),
      startDate: data?.startDate ? dayjs(data?.startDate).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null,
      endDate: data?.endDate ? dayjs(data?.endDate).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null,
      actEmployeeSignedDate: data?.actEmployeeSignedDate ? dayjs(data?.actEmployeeSignedDate).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null,
      firstTransitEmployeeSignedDate: data?.firstTransitEmployeeSignedDate ? dayjs(data?.firstTransitEmployeeSignedDate).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null,
    }));
    api.post('sonnell/specialservice', formdata)
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setReloadSpecialServices(true);
          setData(localizeSpecialServiceDates(response.data.data));
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
  }

  function submit() {
    if (!valid()) {
      return;
    }
    if (data?.id) {
      update();
    } else {
      create();
    }
  }

  useEffect(() => {
    setData(service);
    setLoading(false);
  }, [service]);

  useEffect(() => {
    setRejectionMessage('');
  }, [rejectionModal]);

  useEffect(() => {
    // setIsReadOnly(data?.status !== 0);
    setIsReadOnly(data?.status === 4 || data?.status === 2);
  }, [data]);

  return (
    <>
      <Modal
        width="85%"
        open={service}
        title={(
          <Tag className={data?.id ? 'text-lg' : 'hidden'} color={specialServiceStatusToColor(data?.status)}>{data?.controlNumber}: {t(specialServiceStatusToString(data?.status))}</Tag>
        )}
        footer={[
          <PopconfirmButton
            title={t('are_you_sure_change_status')}
            onConfirm={() => update(1)}
            okText={t('yes')}
            cancelText={t('no')}
            className={`${data?.id && data?.status === 0 ? '' : 'hidden'} bg-primaryColor px-2 mx-1`}
            textColor="text-primaryTextColor"
            loaderColor="bg-primaryTextColor"
            text={t('submit_to_review')}
            loading={loading}
          />,
          <PopconfirmButton
            title={t('are_you_sure_change_status')}
            onConfirm={() => {
              if (valid()) {
                update(3);
              }
            }}
            okText={t('yes')}
            cancelText={t('no')}
            className={`${userPrivilages?.hasOperatorManagerPrivilages && data?.status === 1 ? '' : 'hidden'} bg-primaryColor px-2 mx-1`}
            textColor="text-primaryTextColor"
            loaderColor="bg-primaryTextColor"
            borderColor="border-primaryBorderColor"
            text={t('pre_approve')}
            loading={loading}
          />,
          <PopconfirmButton
            title={t('are_you_sure_change_status')}
            onConfirm={() => {
              // update(2);
              setRejectionModal(true);
            }}
            okText={t('yes')}
            cancelText={t('no')}
            className={`${userPrivilages?.hasOperatorManagerPrivilages && (data?.status === 1 || data?.status === 3) ? '' : 'hidden'} bg-rose-500 hover:bg-rose-600 px-2 mx-1`}
            textColor="text-primaryTextColor"
            loaderColor="bg-primaryTextColor"
            borderColor="bg-rose-500"
            text={t('reject')}
            loading={loading}
          />,
          <PopconfirmButton
            title={t('are_you_sure_change_status')}
            onConfirm={() => {
              if (valid()) {
                update(4);
              }
            }}
            okText={t('yes')}
            cancelText={t('no')}
            className={`${userPrivilages?.hasOperatorManagerPrivilages && data?.status === 3 ? '' : 'hidden'} bg-primaryColor px-2 mx-1`}
            textColor="text-primaryTextColor"
            loaderColor="bg-primaryTextColor"
            borderColor="border-primaryBorderColor"
            text={t('approve')}
            loading={loading}
          />,
          <Button
            className="bg-primaryColor px-2 mx-1"
            textColor="text-primaryTextColor"
            loaderColor="bg-primaryTextColor"
            borderColor="border-primaryBorderColor"
            text={data?.id ? t('update') : t('submit')}
            disabled={data?.status === 2 || data?.status === 4}
            loading={loading}
            onClick={() => submit()}
          />,
        ]}
        onCancel={onClose}
      >
        <Spin spinning={loading}>
          <div>
            <Divider orientation="left">{t('service_requested')}</Divider>
            <div className="gap-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6">
              <AntdDatePicker
                className="col-span-1 md:col-span-2"
                value={dayjs(data?.date)}
                readOnly={isReadOnly || data?.status === 1}
                error={data?.dateError}
                allowClear={false}
                onChange={(obj) => setData({
                  ...data,
                  date: obj.format('MMM DD, YYYY hh:mm A')
                })}
                showTime
                format="MMM DD, YYYY hh:mm A"
                label={t('date')}
              />
              <Textarea
                readOnly={isReadOnly || data?.status === 1}
                className="col-span-1 md:col-span-3 lg:col-span-6"
                label={t('description_of_service_requested')}
                onChange={(obj) => setData({
                  ...data,
                  requestDescription: obj.target.value
                })}
                value={data?.requestDescription}
                error={data?.requestDescriptionError}
              />
            </div>
            
            <Divider orientation="left">{t('service_realized')}</Divider>
            <div className="gap-2 grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6">
              <AntdDatePicker
                readOnly={isReadOnly}
                className="col-span-1 md:col-span-2 lg:col-span-3"
                value={data?.startDate ? dayjs(data?.startDate) : null}
                allowClear={false}
                error={data?.startDateError}
                onChange={(obj) => setData({
                  ...data,
                  startDate: obj.format('MMM DD, YYYY hh:mm A')
                })}
                showTime
                format="MMM DD, YYYY hh:mm A"
                label={t('start_date')}
              />
              <AntdDatePicker
                readOnly={isReadOnly}
                className="col-span-1 md:col-span-2 lg:col-span-3"
                value={data?.endDate ? dayjs(data?.endDate) : null}
                allowClear={false}
                error={data?.endDateError}
                onChange={(obj) => setData({
                  ...data,
                  endDate: obj.format('MMM DD, YYYY hh:mm A')
                })}
                showTime
                format="MMM DD, YYYY hh:mm A"
                label={t('end_date')}
              />
              <AntdInputNumber
                readOnly={isReadOnly}
                label={t('buses')}
                value={data?.busNumber}
                error={data?.busNumberError}
                step={1}
                formatter={(value) => value ? thousandAllowZero(value) : '0'}
                parser={(value) => value ? value.replace(/\$\s?|(,*)/g, '') : '0'}
                onChange={(obj) => setData({
                  ...data,
                  busNumber: obj
                })}
                className="w-full col-span-1 md:col-span-1"
              />
              <AntdInput
                readOnly={isReadOnly}
                label={t('number_of_the_units_used')}
                value={data?.unitNumber}
                error={data?.unitNumberError}
                onChange={(obj) => setData({
                  ...data,
                  unitNumber: obj.target.value
                })}
                className="w-full col-span-1 md:col-span-3 md:mt-1 lg:col-span-5"
              />
              <Textarea
                readOnly={isReadOnly}
                className="col-span-1 md:col-span-3 lg:col-span-6"
                label={t('description_of_service_realized')}
                onChange={(obj) => setData({
                  ...data,
                  realizedDescription: obj.target.value
                })}
                value={data?.realizedDescription}
                error={data?.realizedDescriptionError}
              />
            </div>
            {data?.status >= 3 && (
              <>
                <div className="gap-2 mt-6 grid grid-cols-1 md:grid-cols-3">
                  <AntdInput
                    readOnly={isReadOnly}
                    label={`${t('employee_name')}: ACT/ATI`}
                    value={data?.actEmployeeName}
                    error={data?.actEmployeeNameError}
                    onChange={(obj) => setData({
                      ...data,
                      actEmployeeName: obj.target.value
                    })}
                    className="w-full col-span-1"
                  />
                  <AntdInput
                    readOnly={isReadOnly}
                    label={`${t('employee_number')}: ACT/ATI`}
                    value={data?.actEmployeeNumber}
                    error={data?.actEmployeeNumberError}
                    onChange={(obj) => setData({
                      ...data,
                      actEmployeeNumber: obj.target.value
                    })}
                    className="w-full col-span-1"
                  />
                  <AntdDatePicker
                    readOnly={isReadOnly}
                    className="col-span-1"
                    value={data?.actEmployeeSignedDate ? dayjs(data?.actEmployeeSignedDate) : null}
                    error={data?.actEmployeeSignedDateError}
                    onChange={(obj) => setData({
                      ...data,
                      actEmployeeSignedDate: obj ? obj.format('MMM DD, YYYY') : null
                    })}
                    format="MMM DD, YYYY"
                    label={t('signed_date')}
                  />
                </div>
                  
                <div className="gap-2 mt-6 grid grid-cols-1 md:grid-cols-2">
                  <AntdInput
                    readOnly={isReadOnly}
                    label={`${t('employee_name')}: First Transit PR`}
                    value={data?.firstTransitEmployeeName}
                    error={data?.firstTransitEmployeeNameError}
                    onChange={(obj) => setData({
                      ...data,
                      firstTransitEmployeeName: obj.target.value
                    })}
                    className="w-full col-span-1"
                  />
                  <AntdDatePicker
                    readOnly={isReadOnly}
                    className="col-span-1"
                    value={data?.firstTransitEmployeeSignedDate ? dayjs(data?.firstTransitEmployeeSignedDate) : null}
                    error={data?.firstTransitEmployeeSignedDateError}
                    onChange={(obj) => setData({
                      ...data,
                      firstTransitEmployeeSignedDate: obj ? obj.format('MMM DD, YYYY') : null
                    })}
                    format="MMM DD, YYYY"
                    label={t('signed_date')}
                  />
                </div>
              </>
            )}
            
            <Divider orientation="left" className="mt-6">{t('optional_copy_of_document')}</Divider>
            {!isReadOnly && (
              <Dragger className="mt-4 h-42 md:h-28" disabled={loading} {...draggerProps}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  {t('click_or_drag_content')}
                </p>
              </Dragger>
            )}
            {isReadOnly && !data?.fileUrl && (
              <p className="italic text-gray-500">N/A</p>
            )}
            {data?.fileUrl?.length > 0 && (
              <a href={data?.fileUrl} target="_blank" rel="noreferrer">
                <Button
                  className="bg-primaryColor mb-4 px-2 mt-2"
                  textColor="text-primaryTextColor"
                  loaderColor="bg-primaryTextColor"
                  borderColor="border-primaryBorderColor"
                  text={`${t('preview_file')}: ${data?.fileName}`}
                  loading={loading}
                />
              </a>
            )}
            {data?.rejectionMessage && (
              <>
                <Divider orientation="left">{t('rejection_message')}</Divider>
                <p className="italic text-gray-500">{data?.rejectionMessage}</p>
              </>
            )}
          </div>
        </Spin>
      </Modal>
      <Modal
        open={rejectionModal}
        onCancel={() => setRejectionModal(false)}
        footer={[
          <Button
            className="bg-primaryColor px-2 mx-1"
            textColor="text-primaryTextColor"
            loaderColor="bg-primaryTextColor"
            borderColor="border-primaryBorderColor"
            text={t('submit')}
            loading={loading}
            onClick={() => update(2)}
          />
        ]}
      >
        <Divider orientation="left">{t('rejection_message')}</Divider>
        <AntdInput
          label={t('optional_message')}
          value={rejectionMessage}
          disabled={loading}
          onChange={(obj) => setRejectionMessage(obj.target.value)}
          className="w-full"
        />
      </Modal>
    </>
  );
}

export default SelectedSpecialServicesModal;
